import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Skeleton, Stack, TextField, Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import type { AdmissionApplication, AdmissionCycle, AdmissionField, IdNamePair } from '../../../types/admissions';
import { CLASS_LOCKED_STATUSES } from '../../../types/admissions';
import { admissionApplicationService } from '../../../services/admissions/admissionApplicationService';
import DynamicFieldRenderer from '../form/DynamicFieldRenderer';
import { activeFormSections, isCnicFieldKey, validateDynamicFields, withoutPhoto } from '../form/admissionFormFields';
import { applicationFieldValue } from '../form/applicationFieldValues';
import { useApplicantGuardians } from '../form/useApplicantGuardians';
import { cnicDigits } from '../../../utils/cnic';
import { useDobHint } from '../form/useDobHint';
import ConfirmationDialog from '../../common/ConfirmationDialog';

interface Props {
  open: boolean;
  app: AdmissionApplication;
  cycle: AdmissionCycle;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

/** An answer in the shape the form edits it (dates as yyyy-mm-dd). */
const formValue = (field: AdmissionField, raw: unknown): unknown => {
  if (raw === undefined || raw === null) return '';
  if (field.type === 'date') return String(raw).slice(0, 10);
  if (field.type === 'number') return String(raw);
  return raw;
};

/** Comparable form, so only real edits count as changes. */
const comparable = (key: string, v: unknown): string => {
  if (v === undefined || v === null) return '';
  if (Array.isArray(v)) return JSON.stringify(v);
  const s = String(v).trim();
  return isCnicFieldKey(key) ? cnicDigits(s) : s;
};

/**
 * Staff correct what an applicant entered — every answer on the cycle's
 * form, the class applied for too — up to enrollment. Same form, same
 * rules as the one they filled; only the changed answers are sent, and the
 * server records them on the timeline with the optional reason.
 */
const EditApplicationDialog: React.FC<Props> = ({ open, app, cycle, onClose, onSaved }) => {
  const { enqueueSnackbar } = useSnackbar();
  const guardians = useApplicantGuardians(app, open);
  const [values, setValues] = useState<Record<string, any>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);

  const classLocked = CLASS_LOCKED_STATUSES.includes(app.status);
  const hidden = useMemo(() => new Set(classLocked ? ['classId'] : []), [classLocked]);
  // The photo has its own control (click it in the drawer header).
  const sections = useMemo(
    () => withoutPhoto(activeFormSections(cycle.formTemplate?.sections, hidden)),
    [cycle.formTemplate?.sections, hidden],
  );
  const fields = useMemo(() => sections.flatMap(s => s.fields), [sections]);
  const classOptions = useMemo(
    () => (cycle.allowedClassIds || []).filter((c): c is IdNamePair => typeof c === 'object'),
    [cycle.allowedClassIds],
  );
  const dobHint = useDobHint(cycle.eligibility, classOptions, values?.classId, values?.dob);

  // The answers as they stand, once the parents' records have loaded.
  const initial = useMemo(() => {
    if (!guardians) return null;
    return Object.fromEntries(fields.map(f => [f.key, formValue(f, applicationFieldValue(f.key, { app, guardians }))]));
  }, [fields, app, guardians]);

  useEffect(() => {
    if (open && initial) { setValues(initial); setTouched({}); setReason(''); }
  }, [open, initial]);

  const changed = useMemo(
    () => (initial ? fields.filter(f => comparable(f.key, values[f.key]) !== comparable(f.key, initial[f.key])) : []),
    [fields, values, initial],
  );
  // Only the answers being corrected are held to the form's rules — an
  // older application missing some other answer can still be corrected.
  const errors = useMemo(() => {
    const all = validateDynamicFields(values, sections);
    return Object.fromEntries(changed.filter(f => all[f.key]).map(f => [f.key, all[f.key]]));
  }, [values, sections, changed]);

  // A class on another pathway: the server asks before clearing the marks.
  const [pathwayMove, setPathwayMove] = useState<string | null>(null);

  const save = async (clearMarks = false) => {
    setTouched(Object.fromEntries(fields.map(f => [f.key, true])));
    const firstError = fields.find(f => errors[f.key]);
    if (firstError) {
      formRef.current?.querySelector(`[data-field-key="${firstError.key}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (changed.length === 0) { onClose(); return; }
    setSaving(true);
    try {
      const res = await admissionApplicationService.correct(
        app._id,
        Object.fromEntries(changed.map(f => [f.key, values[f.key]])),
        reason.trim() || undefined,
        clearMarks,
      );
      const n = res.changes.length;
      enqueueSnackbar(n ? `${n} detail${n === 1 ? '' : 's'} corrected` : 'Nothing changed', { variant: 'success' });
      await onSaved();
      onClose();
    } catch (err: any) {
      if (err?.data?.pathwayChange) setPathwayMove(err.message);
      else enqueueSnackbar(err?.message || 'Could not save the corrections', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const familyResolved = !!(app as any).familyResolvedAt;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle component="div" sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={700}>Edit details</Typography>
        <Typography variant="body2" color="text.secondary">
          Correct what was entered for {app.name}. Every change is recorded on the timeline.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {!initial ? (
          <Stack spacing={2}>{[0, 1, 2].map(i => <Skeleton key={i} variant="rounded" height={120} />)}</Stack>
        ) : (
          <Stack spacing={2.5} ref={formRef}>
            {classLocked && (
              <Alert severity="info" variant="outlined">
                The class applied for can't be changed now: an offer has been made or the applicant is waitlisted.
              </Alert>
            )}
            {familyResolved && (
              <Alert severity="info" variant="outlined">
                Parent details are the family's record, so a correction there also shows for any siblings.
              </Alert>
            )}
            <DynamicFieldRenderer
              sections={sections}
              values={values}
              errors={errors}
              touched={touched}
              onChange={(key, v) => setValues(prev => ({ ...prev, [key]: v }))}
              onBlur={(key) => setTouched(prev => ({ ...prev, [key]: true }))}
              classOptions={classOptions}
              fieldHints={dobHint}
              cardSections={false}
              disabled={saving}
            />
            <Box>
              <TextField
                label="Reason for the change (optional)"
                placeholder="e.g. Date of birth corrected from the B-Form"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                fullWidth
                size="small"
                multiline
                minRows={2}
                disabled={saving}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto' }}>
          {changed.length ? `${changed.length} change${changed.length === 1 ? '' : 's'}` : 'No changes yet'}
        </Typography>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" disableElevation onClick={() => save()} disabled={saving || !initial || changed.length === 0}>
          {saving ? 'Saving…' : 'Save corrections'}
        </Button>
      </DialogActions>
      <ConfirmationDialog
        open={!!pathwayMove}
        onClose={() => setPathwayMove(null)}
        onConfirm={() => { setPathwayMove(null); save(true); }}
        title="Move to a class assessed differently?"
        message={pathwayMove ?? ''}
        confirmText="Move and clear marks"
        confirmColor="warning"
        loading={saving}
      />
    </Dialog>
  );
};

export default EditApplicationDialog;
